/**
 * PlaylistTileShell — outer chrome shared by every PlaylistSearchTile
 * variation.
 *
 * Responsibilities:
 *   • 280 px fixed-height card matching ProfileTileCard's footprint
 *   • Faded purple→teal gradient backdrop behind the hero slot
 *   • Meta block (name + "N videos · by Creator") below the hero
 *   • Variant marker (e.g. "V1") in the bottom-right corner — temporary
 *   • Click-through link to /playlist/shared/:id
 *   • Hover-state coordination (450 ms delay) shared with the hover card
 *
 * Each variation supplies:
 *   • `hero`     — the unique thumbnail composition (≈ 156 px tall slot)
 *   • `hoverBody`— the unique hover-card body content (children of
 *                  PlaylistTileHoverShell, mounted alongside the shell)
 *
 * The `hoverActive` boolean is threaded down so animated heroes can
 * pause / sync with the hover state if they want.
 */

import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react'
import type { PlaylistSearchTileProps } from './types'
import PlaylistTileHoverShell from './PlaylistTileHoverShell'

const HOVER_DELAY_MS = 450

interface PlaylistTileShellProps extends PlaylistSearchTileProps {
  /** Variation hero render — the unique thumbnail composition. Receives
   * the array of thumbnail URLs and a `hoverActive` flag (true once the
   * 450 ms hover delay has elapsed). */
  renderHero: (args: {
    previewThumbnailUrls: string[]
    hoverActive: boolean
  }) => React.ReactNode
  /** Variation hover-body render — fills the hover panel. */
  renderHoverBody: (args: {
    playlistId: string
    playlistName: string
    metaLine: string
    previewThumbnailUrls: string[]
    previewContentTitles: string[]
    itemCount: number | null
  }) => React.ReactNode
}

function PlaylistTileShell ({
  playlistId,
  playlistName,
  itemCount,
  previewThumbnailUrls,
  previewContentTitles,
  creatorDisplayName,
  variantLabel,
  renderHero,
  renderHoverBody,
}: PlaylistTileShellProps): React.ReactElement {
  const [mouseIn, setMouseIn] = useState(false)
  const [hoverActive, setHoverActive] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (mouseIn) {
      timerRef.current = setTimeout(() => setHoverActive(true), HOVER_DELAY_MS)
    } else {
      if (timerRef.current) clearTimeout(timerRef.current)
      setHoverActive(false)
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [mouseIn])

  const handleMouseEnter = useCallback(() => setMouseIn(true), [])
  const handleMouseLeave = useCallback(() => setMouseIn(false), [])

  const metaLine = useMemo(() => {
    return [
      itemCount != null ? `${itemCount} videos` : null,
      `by ${creatorDisplayName || 'Anonymous'}`,
    ].filter(Boolean).join(' · ')
  }, [itemCount, creatorDisplayName])

  return (
    <div
      className="playlist-search-tile"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <a
        href={`/playlist/shared/${playlistId}`}
        className="playlist-search-tile__link"
      >
        <div className="playlist-search-tile__hero">
          {renderHero({ previewThumbnailUrls, hoverActive })}
        </div>

        <div className="playlist-search-tile__meta">
          <span className="playlist-search-tile__name">{playlistName}</span>
          {metaLine && (
            <span className="playlist-search-tile__info">{metaLine}</span>
          )}
        </div>

        {variantLabel ? (
          <span
            className="playlist-search-tile__variant-marker"
            aria-hidden="true"
          >
            {variantLabel}
          </span>
        ) : null}
      </a>

      <PlaylistTileHoverShell hoverActive={hoverActive}>
        {renderHoverBody({
          playlistId,
          playlistName,
          metaLine,
          previewThumbnailUrls,
          previewContentTitles,
          itemCount,
        })}
      </PlaylistTileHoverShell>
    </div>
  )
}

export default React.memo(PlaylistTileShell)
